/**
 * Single notification row with deep-link CTA.
 */

import React from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Check, DeleteOutline } from "@mui/icons-material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  CATEGORY_ACCENT,
  getCtaLabel,
  getTypeLabel,
  isActionRequired,
  resolveNotificationCategory,
} from "../utils/taxonomy.js";
import {
  getAccentForNotification,
  getNotificationIconComponent,
} from "../utils/icons.js";

dayjs.extend(relativeTime);

const NotificationRow = ({
  notification,
  onOpen,
  onMarkRead,
  onDelete,
  isMobile = false,
}) => {
  const Icon = getNotificationIconComponent(notification);
  const accent = getAccentForNotification(notification, CATEGORY_ACCENT);
  const category = resolveNotificationCategory(notification);
  const actionRequired = isActionRequired(notification);
  const cta = getCtaLabel(notification);

  const actions = (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      onClick={(e) => e.stopPropagation()}
      sx={{ flexShrink: 0 }}
    >
      <Button
        size="small"
        variant="outlined"
        onClick={() => onOpen(notification)}
        sx={{
          textTransform: "none",
          whiteSpace: "nowrap",
          ...(isMobile && { flex: 1 }),
        }}
      >
        {cta}
      </Button>
      <Tooltip title="Mark as read">
        <span>
          <IconButton
            size="small"
            disabled={notification.isRead}
            onClick={() => onMarkRead(notification.id)}
          >
            <Check fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Remove">
        <IconButton
          size="small"
          color="error"
          onClick={() => onDelete(notification.id)}
        >
          <DeleteOutline fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  return (
    <Box
      onClick={() => onOpen(notification)}
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? 1 : 2,
        alignItems: isMobile ? "stretch" : "flex-start",
        px: isMobile ? 1.5 : 2,
        py: isMobile ? 1.5 : 1.75,
        cursor: "pointer",
        bgcolor: notification.isRead ? "transparent" : "background.paper",
        borderBottom: "1px solid",
        borderColor: "divider",
        transition: "background-color 0.15s ease",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
        <Box
          sx={{
            width: isMobile ? 36 : 42,
            height: isMobile ? 36 : 42,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: `${accent}18`,
            color: accent,
            flexShrink: 0,
          }}
        >
          <Icon fontSize="small" />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{ mb: 0.5 }}
          >
            <Typography
              variant={isMobile ? "body1" : "subtitle1"}
              fontWeight={notification.isRead ? 500 : 700}
              sx={{ lineHeight: 1.3 }}
            >
              {notification.title}
            </Typography>
            {!notification.isRead && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: "info.main",
                  flexShrink: 0,
                }}
              />
            )}
          </Stack>

          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
            {actionRequired && (
              <Chip
                size="small"
                label="Action required"
                sx={{
                  height: 20,
                  fontSize: 11,
                  bgcolor: `${CATEGORY_ACCENT.action_required}22`,
                  color: "#9A7A12",
                  fontWeight: 600,
                }}
              />
            )}
            <Chip
              size="small"
              label={getTypeLabel(notification.type)}
              variant="outlined"
              sx={{ height: 20, fontSize: 11 }}
            />
          </Stack>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              mb: 0.75,
              fontSize: isMobile ? 13 : "inherit",
            }}
          >
            {notification.message}
          </Typography>

          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary">
              {dayjs(notification.created_at).fromNow()}
            </Typography>
            {notification.entityId && !isMobile && (
              <Typography variant="caption" color="text.secondary">
                {notification.entityType}: {notification.entityId}
              </Typography>
            )}
            <Typography
              variant="caption"
              sx={{ color: CATEGORY_ACCENT[category] || "text.secondary", fontWeight: 600 }}
            >
              {category.replace(/_/g, " ")}
            </Typography>
          </Stack>
        </Box>

        {!isMobile && actions}
      </Box>

      {isMobile && actions}
    </Box>
  );
};

export default NotificationRow;
